import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import "./DashboardCajero.css";
import "./PanelCobro.css";
import CobroExternos from './CobroExternos'

// 1. IMPORTAMOS LA NUEVA FUNCIÓN MATEMÁTICA
import NavbarCajero from './NavbarCajero';
import MenuModulos from './MenuModulos';
import EncabezadoAlumno from './EncabezadoAlumno';
import VistaBusqueda from './VistaBusqueda';
import VistaCobroAlumno from './VistaCobroAlumno';
import VistaValidarPago from './VistaValidarPago';
import VistaHistorial from './VistaHistorial';
import CobroAspirantes from "./CobroAspirantes";
import CobroExAlumnos from "./CobroExAlumnos";
import { esAdministrador, puedeCobrarConPinpad, puedeValidarPago } from "../utilities/permisos";

import useBusquedaAlumno from '../hooks/useBusquedaAlumno';
import useCarrito from '../hooks/useCarrito';
import useValidacionPago from '../hooks/useValidacionPago';
import useHistorialPagos from '../hooks/useHistorialPagos';

import VistaReportes from './VistaReportes';
import VistaUsuarios from './VistaUsuarios';
import { API_BASE } from "../config";
import { obtenerToken, sesionExpirada, cerrarSesion, LLAVE_VISTA } from "../utilities/sesion";
import { apiFetch } from "../utilities/apiFetch";

const VISTAS_DE_MODULO = ["busqueda", "externos", "reportes", "exalumnos", "aspirantes", "usuarios"];

const leerVistaGuardada = () => {
  try {
    const guardada = sessionStorage.getItem(LLAVE_VISTA);
    return VISTAS_DE_MODULO.includes(guardada) ? guardada : "busqueda";
  } catch (error) {
    return "busqueda";
  }
};

const DashboardCajero = () => {
  const history = useHistory();

  // --- ESTADOS PARA LOS CATÁLOGOS DINÁMICOS ---
  const [catalogoPlanteles, setCatalogoPlanteles] = useState([]);
  const [catalogoPeriodos, setCatalogoPeriodos] = useState([]);
  
  // Estados de sesión
  const [cajeroId, setCajeroId] = useState("");
  const [plantelId, setPlantelId] = useState("");
  const [nombreCajero, setNombreCajero] = useState("");
  const [nombrePlantel, setNombrePlantel] = useState("");

  // Solo para rol Administrador: plantel que esta consultando (Ex-alumnos,
  // Externos), sin que cambie su plantel real de sesion. Por defecto es el
  // suyo propio. Es solo de lectura: cualquier cobro sigue quedando a
  // nombre del plantel real del administrador (Oficinas Centrales).
  const [plantelRevisionId, setPlantelRevisionId] = useState("");

  // INSTANCIAR EL HOOK DE BÚSQUEDA
  const {
    tipoBusqueda,
    criterioBusqueda,
    setCriterioBusqueda,
    filtroSemestre,
    setFiltroSemestre,
    filtroPlantelOrigen,
    setFiltroPlantelOrigen,
    filtroPeriodo,
    setFiltroPeriodo,
    resultados,
    setResultados,
    isLoading,
    mensaje,
    cambiarTipoBusqueda,
    handleBuscarAlumno
  } = useBusquedaAlumno(cajeroId, plantelId);

  const {
    carrito,
    setCarrito,
    totalCarrito,
    agregarAlCarrito,
    quitarDelCarrito,
    procesarPagoCaja,
    generarFichaPdf,
    modalExito,      // <--- Nuevo
    setModalExito    // <--- Nuevo
  } = useCarrito();

  // --- ESTADOS PARA EL PUNTO DE VENTA Y NAVEGACIÓN ---
  // Al refrescar (F5) se conserva el modulo en el que estaba el cajero. Solo se
  // restauran pantallas de modulo; las que dependen de un alumno seleccionado
  // (cobro, historial, registrar_pago) vuelven a la busqueda.
  const [vistaActiva, setVistaActiva] = useState(leerVistaGuardada); // 'busqueda', 'cobro', 'registrar_pago', 'historial'
  const [cajeroRol, setCajeroRol] = useState("");
  const [alumnoSeleccionado, setAlumnoSeleccionado] = useState(null);
  const [catalogoConceptos, setCatalogoConceptos] = useState([]);
  const [loadingCobro, setLoadingCobro] = useState(false);

  // INSTANCIAR EL HOOK DE VALIDACIÓN DE PAGO
  const {
    referenciasPendientes,
    cargandoReferencias,
    mostrarModalValidacion,
    setMostrarModalValidacion,
    refSeleccionada,
    folioBanco,
    setFolioBanco,
    buscarReferenciasPendientes,
    confirmarValidacionPago,
    abrirModalValidacion,
    bancoSeleccionado,
    setBancoSeleccionado,
    loadingValidacion,
    modoModal,
    cerrarModal,
    abrirModalComplemento,
    cobrarFaltantePinpad,
    cobrarFaltanteTPV,
    calcularFaltante,
    tienePinpad
  } = useValidacionPago(cajeroId, alumnoSeleccionado);

  // INSTANCIAR EL HOOK DE HISTORIAL
  const {
    historialPagos,
    cargandoHistorial,
    obtenerHistorialAlumno
  } = useHistorialPagos();

  // 1. Cargar datos del cajero al iniciar
  useEffect(() => {
    const idGuardado = localStorage.getItem("cajero_id");
    if (!idGuardado || !obtenerToken() || sesionExpirada()) {
      cerrarSesion();
      history.push("/");
    } else {
      setCajeroId(idGuardado);
      setCajeroRol(localStorage.getItem("cajero_rol") || "");
      const idPlantelSesion = localStorage.getItem("cajero_plantel") || "";
      setPlantelId(idPlantelSesion);
      setPlantelRevisionId(idPlantelSesion); // por defecto, revisa su propio plantel
      setNombreCajero(localStorage.getItem("cajero_nombre") || "Cajero");

      let plantelString = localStorage.getItem("cajero_nombre_plantel") || "Plantel";
      plantelString = plantelString.replace(/CECYTEJ/ig, "").trim();
      setNombrePlantel(plantelString);
    }
  }, [history]);

  // Nombre a mostrar del plantel que el administrador esta revisando (si es
  // el mismo que el suyo, reutiliza el nombre ya cargado de sesion).
  const nombrePlantelRevision = plantelRevisionId === plantelId
    ? nombrePlantel
    : (catalogoPlanteles.find((p) => String(p.id) === String(plantelRevisionId))?.nombre_plantel || nombrePlantel);

  // El catalogo compartido de planteles excluye el propio (ej. Oficinas
  // Centrales, id 2) por diseno (ver Mtd_ObtenerPlanteles). Para el selector
  // del administrador, siempre agregamos su propio plantel como primera
  // opcion si no viniera ya incluido, para que "ver mi propio plantel" sea
  // una opcion real y visible.
  const opcionesSelectorPlantel = plantelId && !catalogoPlanteles.some((p) => String(p.id) === String(plantelId))
    ? [{ id: plantelId, nombre_plantel: nombrePlantel }, ...catalogoPlanteles]
    : catalogoPlanteles;

  useEffect(() => {
    if (!VISTAS_DE_MODULO.includes(vistaActiva)) return;
    try {
      sessionStorage.setItem(LLAVE_VISTA, vistaActiva);
    } catch (error) {
      // sin sessionStorage simplemente no se recuerda el modulo
    }
  }, [vistaActiva]);

  // 2. Cargar los catálogos desde la BD al iniciar
  useEffect(() => {
    const obtenerCatalogos = async () => {
      try {
        // A) Cargar Conceptos (contexto caja: incluye los de uso exclusivo en ventanilla)
        const resConceptos = await fetch(`${API_BASE}/wMultipagosObtenerConceptos.php?contexto=caja`);
        const dataConceptos = await resConceptos.json();
        if (dataConceptos.success) setCatalogoConceptos(dataConceptos.data);

        // B) Cargar Planteles (URL Corregida)
        const resPlanteles = await apiFetch(`${API_BASE}/wMultipagosObtenerPlanteles.php`);
        const dataPlanteles = await resPlanteles.json();
        if (dataPlanteles.success) setCatalogoPlanteles(dataPlanteles.data);

        // C) Cargar Periodos/Ciclos (URL Corregida)
        const resPeriodos = await apiFetch(`${API_BASE}/wMultipagosObtenerPeriodos.php`);
        const dataPeriodos = await resPeriodos.json();
        if (dataPeriodos.success) setCatalogoPeriodos(dataPeriodos.data);

      } catch (error) {
        console.error("Error al cargar los catálogos:", error);
      }
    };
    
    obtenerCatalogos();
  }, []);

  const handleCerrarSesion = () => {
    cerrarSesion();
    history.push("/");
  };

  const handleSeleccionarAlumno = (alumno, accion) => {
    setAlumnoSeleccionado(alumno);
    setVistaActiva(accion);
    setResultados([]); 
    setCriterioBusqueda(""); 
  };

  const handleCancelarSeleccion = () => {
    setAlumnoSeleccionado(null);
    setVistaActiva("busqueda");
    setCarrito([]); 
  };

  // --- LÓGICA PARA REGISTRAR / VALIDAR PAGOS (VENTANILLA) ---
  useEffect(() => {
    if (vistaActiva === 'registrar_pago' && alumnoSeleccionado) {
      buscarReferenciasPendientes(alumnoSeleccionado.matricula);
    }
  }, [vistaActiva, alumnoSeleccionado]);

  // --- LÓGICA PARA HISTORIAL DE PAGOS ---
  useEffect(() => {
    if (vistaActiva === 'historial' && alumnoSeleccionado) {
      obtenerHistorialAlumno(alumnoSeleccionado.matricula);
    }
  }, [vistaActiva, alumnoSeleccionado]);

  // FUNCIÓN INTERMEDIARIA CON TRY/CATCH/FINALLY
  const handleEjecutarCobroCaja = async () => {
    setLoadingCobro(true);
    try {
      await procesarPagoCaja(alumnoSeleccionado, cajeroId); 
    } catch (error) {
      console.error("Error en la operación de cobro:", error);
    } finally {
      setLoadingCobro(false); 
    }
  };

  const handleGenerarFichaPdf = async () => {
    setLoadingCobro(true);
    try {
      await generarFichaPdf(alumnoSeleccionado, cajeroId);
    } finally {
      setLoadingCobro(false);
    }
  };

  return (
    <div className="Dashboard-container">

      <NavbarCajero
        nombreCajero={nombreCajero}
        nombrePlantel={nombrePlantel}
        onCerrarSesion={handleCerrarSesion}
        esAdministrador={esAdministrador(cajeroRol)}
        catalogoPlanteles={opcionesSelectorPlantel}
        plantelRevisionId={plantelRevisionId}
        onCambiarPlantelRevision={setPlantelRevisionId}
      />

      <main className="Dashboard-main">

        {/* --- MENÚ PRINCIPAL (ALUMNOS VS EXTERNOS) --- */}
        {!alumnoSeleccionado && (
          <MenuModulos
            vistaActiva={vistaActiva}
            setVistaActiva={setVistaActiva}
            setAlumnoSeleccionado={setAlumnoSeleccionado}
            cajeroRol={cajeroRol}
          />
        )}

        {/* --- VISTA COBRO A EXTERNOS --- */}
        {vistaActiva === "externos" && (
          <CobroExternos
            user={{
              id_plantel: plantelRevisionId,
              id_usuario: typeof cajeroId !== 'undefined' ? cajeroId : 1,
              nombre_plantel: nombrePlantelRevision
            }}
          />
        )}

        {/* --- VISTA REPORTES --- */}
        {vistaActiva === 'reportes' && (
          <VistaReportes />
        )}

        {/* --- VISTA USUARIOS (solo Administrador) --- */}
        {vistaActiva === 'usuarios' && (
          <VistaUsuarios />
        )}

        {/* --- VISTA MÓDULO EX-ALUMNOS --- */}
        {vistaActiva === 'exalumnos' && (
          <CobroExAlumnos
            user={{
              id_plantel: plantelRevisionId,
              id_usuario: typeof cajeroId !== 'undefined' ? cajeroId : 1,
              nombre_plantel: nombrePlantelRevision
            }}
            catalogoConceptos={catalogoConceptos}
          />
        )}

        {/* --- VISTA MÓDULO ASPIRANTES --- */}
        {vistaActiva === 'aspirantes' && (
          <CobroAspirantes 
            user={{ 
              id_plantel: plantelId, 
              id_usuario: typeof cajeroId !== 'undefined' ? cajeroId : 1,
              nombre_plantel: nombrePlantel 
            }} 
          />
        )}

        {/* --- VISTA 1: BÚSQUEDA --- */}
        {vistaActiva === "busqueda" && (
          <VistaBusqueda 
            cajeroRol={cajeroRol}
            tipoBusqueda={tipoBusqueda}
            cambiarTipoBusqueda={cambiarTipoBusqueda}
            criterioBusqueda={criterioBusqueda}
            setCriterioBusqueda={setCriterioBusqueda}
            isLoading={isLoading}
            handleBuscarAlumno={handleBuscarAlumno}
            filtroPlantelOrigen={filtroPlantelOrigen}
            setFiltroPlantelOrigen={setFiltroPlantelOrigen}
            plantelId={plantelId}
            nombrePlantel={nombrePlantel}
            catalogoPlanteles={catalogoPlanteles}
            filtroSemestre={filtroSemestre}
            setFiltroSemestre={setFiltroSemestre}
            filtroPeriodo={filtroPeriodo}
            setFiltroPeriodo={setFiltroPeriodo}
            catalogoPeriodos={catalogoPeriodos}
            mensaje={mensaje}
            resultados={resultados}
            handleSeleccionarAlumno={handleSeleccionarAlumno}
          />
        )}

        {/* --- ENCABEZADO FIJO DE ALUMNO Y VISTAS DE OPERACIÓN --- */}
        {alumnoSeleccionado && (
          <div className="PuntoDeVenta-container">

            {/* Cabecera dinámica que se queda arriba en cualquier acción */}
            <EncabezadoAlumno 
              alumno={alumnoSeleccionado} 
              vistaActiva={vistaActiva} 
              onVolver={handleCancelarSeleccion} 
            />   

            {/* --- VISTA 2: ÁREA DE COBRO (NUEVO DISEÑO 2 COLUMNAS + TICKET) --- */}
            {vistaActiva === "cobro" && (
              <VistaCobroAlumno 
                catalogoConceptos={catalogoConceptos}
                carrito={carrito}
                agregarAlCarrito={agregarAlCarrito}
                quitarDelCarrito={quitarDelCarrito}
                totalCarrito={totalCarrito}
                procesarPagoCaja={handleEjecutarCobroCaja} 
                generarFichaPdf={handleGenerarFichaPdf}
                loading={loadingCobro} 
                // =========================================================
                // 🔥 NUEVO: PASAMOS EL ESTADO Y SETTER DEL MODAL DE ÉXITO
                // =========================================================
                modalExito={modalExito}
                setModalExito={setModalExito}
                // =========================================================
                puedeCobrar={puedeCobrarConPinpad(cajeroRol)}
                soloLectura={esAdministrador(cajeroRol)}
              />
            )}

            {/* --- VISTA 3: REGISTRAR PAGO --- */}
            {vistaActiva === "registrar_pago" && (
              <VistaValidarPago 
                cargandoReferencias={cargandoReferencias}
                referenciasPendientes={referenciasPendientes}
                alumnoSeleccionado={alumnoSeleccionado}
                abrirModalValidacion={abrirModalValidacion}
                abrirModalComplemento={abrirModalComplemento}
                mostrarModalValidacion={mostrarModalValidacion}
                refSeleccionada={refSeleccionada}
                folioBanco={folioBanco}
                setFolioBanco={setFolioBanco}
                setMostrarModalValidacion={setMostrarModalValidacion}
                confirmarValidacionPago={confirmarValidacionPago}
                bancoSeleccionado={bancoSeleccionado}
                setBancoSeleccionado={setBancoSeleccionado}
                loadingValidacion={loadingValidacion}
                modoModal={modoModal}
                cerrarModal={cerrarModal}
                cobrarFaltantePinpad={cobrarFaltantePinpad}
                cobrarFaltanteTPV={cobrarFaltanteTPV}
                calcularFaltante={calcularFaltante}
                tienePinpad={tienePinpad}
                puedeValidar={puedeValidarPago(cajeroRol)}
                soloLectura={esAdministrador(cajeroRol)}
              />
            )}

            {/* --- VISTA 4: HISTORIAL --- */}
            {vistaActiva === "historial" && (
              <VistaHistorial 
                cargandoHistorial={cargandoHistorial}
                historialPagos={historialPagos}
              />
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default DashboardCajero;